import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { PLANT_DISTANCES_CACHE_TIME } from '@core/constants/common';
import type { Address } from '@core/types/api/Address/Address';
import { NewAddress } from '@core/types/api/Address/Address';
import type { DistanceDurationPair } from '@core/types/api/DistanceDurationPair/DistanceDurationPair';
import { DistanceFromPointsInput } from '@core/types/api/DistanceFromPointsInput/DistanceFromPointsInput';
import type { DistanceResult } from '@core/types/api/DistanceResult/DistanceResult';
import Enums from '@core/types/api/Enums';
import type { Plant } from '@core/types/api/Plant/Plant';
import { hasValue } from '@core/utils/equality';

import { useSlabMutation } from '../useSlabMutation';
import { useSlabQuery } from '../useSlabQuery';

export const usePlantDistances = ({
  address,
  extraPlants = [],
}: {
  address: Partial<Address> | undefined;
  extraPlants?: Plant[];
}): {
  plants: Plant[];
  distanceByPlantId: Map<string, DistanceDurationPair | null>;
  isLoading: boolean;
  isPlantListLoaded: boolean;
} => {
  const { mutateAsync: fetchDistances } = useSlabMutation('POST maps distance from points');

  const { isLoading: isLoadingPlants, data: rawPlantList } = useSlabQuery('GET plants', {
    queryParams: {
      page: 0,
      perPage: 0,
      filterBy: [{ name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] }],
    },
  });

  const activePlants = rawPlantList?.items;
  const extraPlantIds = extraPlants.map((p) => p.id).join(',');

  const archivedPlants = useMemo(() => {
    const activeIds = new Set((activePlants ?? []).map((p) => p.id));
    return extraPlants.filter((p) => !activeIds.has(p.id));
  }, [activePlants, extraPlantIds]);

  const plants = useMemo(
    () => [...(activePlants ?? []), ...archivedPlants],
    [activePlants, archivedPlants],
  );

  const destination = hasValue(address) ? NewAddress(address).toLatLngPairString() : undefined;
  const hasOrigin = (p: Plant): boolean => hasValue(p.address.toLatLngPairString());

  // Archived plants get their own request, so the active one stays shared with readers that
  // never see them (the dropdown, project autoselect).
  const groups = [(activePlants ?? []).filter(hasOrigin), archivedPlants.filter(hasOrigin)];

  const queries = useQueries({
    queries: groups.map((group, groupIndex) => {
      const origins = group.map((p) => p.address.toLatLngPairString() ?? '');
      return {
        queryKey:
          groupIndex === 0
            ? ['POST maps distance from points', destination, ...origins]
            : ['POST maps distance from points', destination, 'archived', ...origins],
        queryFn: ({ signal }: { signal: AbortSignal }): Promise<DistanceResult> =>
          fetchDistances({
            args: {
              body: new DistanceFromPointsInput({ origins, destinations: [destination ?? ''] }),
            },
            signal,
          }),
        enabled: hasValue(activePlants) && hasValue(destination) && origins.length > 0,
        staleTime: PLANT_DISTANCES_CACHE_TIME,
        gcTime: PLANT_DISTANCES_CACHE_TIME,
        // A background refetch would rewrite distances the form already holds.
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      };
    }),
  });

  const distanceByPlantId = useMemo(() => {
    const map = new Map<string, DistanceDurationPair | null>();
    groups.forEach((group, groupIndex) => {
      const distances = queries[groupIndex].data;
      if (!hasValue(distances)) {
        return;
      }
      group.forEach((plant, originIndex) => {
        map.set(plant.id, distances.resultByIndex(originIndex, 0) ?? null);
      });
    });
    return map;
  }, [queries[0].data, queries[1].data, plants]);

  return {
    plants,
    distanceByPlantId,
    isLoading: isLoadingPlants || queries.some((q) => q.isLoading),
    isPlantListLoaded: hasValue(rawPlantList),
  };
};
